<template>
  <card id="profile" :title="$t('profile:title')">
    <small slot="card-header-right" class="text-muted font-italic">{{ $t('profile:required') }}</small>

    <template slot="card-body">
      <!-- Fake input to prevent saved password to be filled -->
      <input id="fakeEmail" type="email" class="d-none">
      <input id="fakePassword" type="password" class="d-none">

      <div class="form-row">
        <div class="form-group col-md-6">
          <label for="login">{{ $t('profile:login') | capitalize }}</label>
          <input id="login"
                 v-model="login"
                 type="text"
                 class="form-control"
                 disabled
          >
          <small class="form-text text-muted">{{ $t('profile:loginNoedit') | capitalize }}</small>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group col-md-6">
          <label for="firstName">{{ $t('profile:firstName') | capitalize }}</label>
          <input id="firstName"
                 v-model="firstName"
                 type="text"
                 class="form-control"
          >
        </div>

        <div class="form-group col-md-6">
          <label for="lastName">{{ $t('profile:lastName') | capitalize }}</label>
          <input id="lastName"
                 v-model="lastName"
                 type="text"
                 class="form-control"
          >
        </div>
      </div>

      <div class="form-row">
        <div v-if="isRole.superAdmin" class="form-group col-md-6">
          <label for="customerId">{{ $t('profile:customer') | capitalize }}</label>
          <select id="customerId" v-model="customer" class="form-control">
            <option v-for="c in customers"
                    :key="c.id"
                    :value="c.id"
            >
              {{ c.name }}
            </option>
          </select>
        </div>
      </div>

      <h3 class="pt-2 my-4">{{ $t('profile:contactInformation') | capitalize }}</h3>

      <div class="form-row">
        <div class="form-group col-md-6">
          <label for="email">{{ $t('profile:email') | capitalize }} *</label>
          <input id="email"
                 v-model="email"
                 v-validate="'required|email'"
                 :class="{ 'is-invalid': errors.has('email') }"
                 class="form-control"
                 name="email"
                 type="email"
          >
          <div class="invalid-feedback">{{ errors.first('email') }}</div>
        </div>

        <div class="form-group col-md-6">
          <label for="phone">{{ $t('profile:phone') | capitalize }}</label>
          <input id="phone"
                 v-model="phone"
                 name="phone"
                 type="text"
                 class="form-control"
          >
        </div>
      </div>

      <h3 class="pt-2 my-4">{{ $t('profile:picture') | capitalize }}</h3>

      <div class="form-row">
        <div class="form-group col-md-8">
          <label for="avatar">{{ $t('profile:pictureUrl') | capitalize }}</label>
          <input id="avatar"
                 v-model="avatar"
                 v-validate="'url'"
                 :class="{ 'is-invalid': errors.has('avatar') }"
                 name="avatar"
                 type="url"
                 class="form-control"
          >
          <small class="form-text text-muted">{{ $t('profile:pictureUrlComment') | capitalize }}</small>
          <div class="invalid-feedback">{{ errors.first('avatar') }}</div>
        </div>

        <div class="col-md-4">
          <avatar :name="login"
                  :picture-url="errors.has('avatar') ? '' : avatarThumbUrl"
                  class="ml-5"
                  size="lg"
          />
        </div>
      </div>

      <h3 class="pt-2 my-4">{{ $t('profile:account') | capitalize }}</h3>

      <div class="form-row">
        <div v-if="isRole.admin && !isAuthenticatedUser && hasHigherRole" class="form-group col-md-6">
          <label for="role">{{ $t('profile:role') | capitalize }}</label>
          <select id="role" v-model="role" class="form-control">
            <option v-for="r in availableRoles"
                    :key="r.level"
                    :value="r.name"
            >
              {{ r.name }}
            </option>
          </select>
        </div>

        <div v-else-if="isAuthenticatedUser" class="form-group col-md-6">
          <label for="role">{{ $t('profile:role') | capitalize }}</label>
          <select id="role" class="form-control" disabled>
            <option>{{ user.role }}</option>
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group col-md-6">
          <label for="password">{{ $t('profile:password') | capitalize }}<span v-if="password !== ''"> *</span></label>
          <input id="password"
                 ref="password"
                 v-model="password"
                 name="password"
                 type="password"
                 class="form-control"
          >
          <password-strength :password="password"
                             class="mr-2"
          />
        </div>

        <div class="form-group col-md-6">
          <label for="cpassword">{{ $t('profile:rePassword') | capitalize }}<span v-if="password !== ''"> *</span></label>
          <input id="cpassword"
                 v-model="cpassword"
                 v-validate="{ rules: { required: password !== '', confirmed: password }}"
                 :class="{ 'is-invalid': errors.has('cpassword') }"
                 :data-vv-as="$t('profile:password')"
                 type="password"
                 name="cpassword"
                 class="form-control"
          >
          <div class="invalid-feedback">{{ errors.first('cpassword') }}</div>
        </div>
      </div>

      <div v-if="isRole.admin && hasHigherRole && !isAuthenticatedUser">
        <h3 class="pt-2 my-4">{{ $t('profile:limitation') | capitalize }}</h3>

        <div class="form-group">
          <label class="control-label d-block">{{ $t('profile:activation') | capitalize }}</label>
          <input id="activationEnabled"
                 v-model="enabled"
                 v-validate="'required'"
                 :data-vv-as="$t('profile:activation')"
                 :class="{ 'is-invalid': errors.has('enabled') }"
                 :value="true"
                 type="radio"
                 name="enabled"
          >
          <label for="activationEnabled" class="control-label">{{ $t('profile:activationEnabled') | capitalize }}</label>

          <input id="activationDisabled"
                 v-model="enabled"
                 :class="{ 'is-invalid': errors.has('enabled') }"
                 :value="false"
                 type="radio"
                 name="enabled"
          >
          <label for="activationDisabled" class="control-label">{{ $t('profile:activationDisabled') | capitalize }}</label>

          <div class="invalid-feedback">{{ errors.first('enabled') }}</div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="expirationDate">{{ $t('profile:expirationDate') | capitalize }}</label>
            <div class="input-group">
              <flat-pickr id="expirationDate"
                          v-model="expirationDate"
                          :config="fpConfig"
                          class="form-control"
              />
              <div class="input-group-append">
                <button id="resetDateButton" class="btn btn-flat btn-sm" type="button" @click="expirationDate = null"><i class="icon-remove" /></button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="isAuthenticatedUser && expirationDate">
        <h3 class="pt-2">{{ $t('profile:limitation') | capitalize }}</h3>

        <div class="form-group">
          <label for="expirationDate">{{ $t('profile:expirationDate') | capitalize }}</label>
          <div>{{ expirationDate | date }}</div>
        </div>
      </div>
    </template>

    <template slot="card-footer">
      <button id="profileForm-update"
              :disabled="!isAuthenticatedUser && !hasHigherRole"
              :title="(!isAuthenticatedUser && !hasHigherRole ? $t('profile:canEditHigherUser') : '') | capitalize"
              class="btn btn-primary"
              @click="updateUser"
      >
        {{ $t('profile:update') }}
      </button>
    </template>
  </card>
</template>

<script>
import { find, filter } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

export default {
  titleKey: 'profile',

  props: {
    id: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      user: {},

      role: '',
      customer: '',

      login: '',
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      avatar: null,
      avatarThumbUrl: null,
      password: '',
      cpassword: null,
      expirationDate: null,
      enabled: null,

      // In data to be modified if needed
      fpConfig: {
        wrap: true,
        altInput: true,
        altFormat: 'Y/m/d',
        dateFormat: 'U'
      }
    }
  },

  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters(['authenticatedUser', 'customers', 'roles']),

    isAuthenticatedUser () {
      return Number(this.id) === this.authenticatedUser.id
    },

    authenticatedUserRole () {
      return find(this.roles, role => role.name === this.authenticatedUser.role) || {}
    },

    availableRoles () {
      return filter(this.roles, role => role.level <= this.authenticatedUserRole.level)
    },

    userRole () {
      return find(this.roles, role => role.name === this.user.role) || {}
    },

    hasHigherRole () {
      return this.authenticatedUserRole.level >= this.userRole.level
    }
  },

  watch: {
    // Update data when user or customer change in url to update links
    id () {
      this.getUser()
    }
  },

  created () {
    this.getUser()
  },

  methods: {
    async getUser () {
      if (this.isRole.superAdmin) {
        // Get all customers to fill the list, the roles have been loaded in Squeleton.vue
        this.$store.dispatch('getCustomers')
      }

      let error, response
      [error, response] = await to(this.$api.getUser(this.$store.getters.application, this.id))
      if (error || !response) {
        if (error.response && error.response.status === 404) eventBus.$emit('request:404')
        if (error.response && error.response.status === 403) eventBus.$emit('request:403')
        return
      }

      this.user = response.data
      this.customer = response.data.customer.id
      this.role = response.data.role

      this.login = response.data.login
      this.firstName = response.data.firstName
      this.lastName = response.data.lastName
      this.email = response.data.email
      this.phone = response.data.phone
      this.avatar = response.data.avatar
      this.avatarThumbUrl = response.data.avatar
      if (this.isAuthenticatedUser) {
        this.expirationDate = response.data.expirationDate ? '' + response.data.expirationDate : null
      } else {
        // Need to set date in second and as a string to prevent a big mess up between get and set
        this.expirationDate = response.data.expirationDate ? '' + response.data.expirationDate / 1000 : null
      }
      this.enabled = response.data.enabled
    },

    async updateUser () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      const user = {
        customer: { id: this.customer },
        role: this.role,
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phone: this.phone,
        avatar: this.avatar,
        expirationDate: this.expirationDate ? this.expirationDate * 1000 : null,
        enabled: this.enabled
      }

      if (this.password) {
        user.password = this.password
      }

      // Authenticated user cannot change its expiration date or enabled attributes
      if (this.isAuthenticatedUser) {
        delete user.expirationDate
        delete user.enabled
        delete user.role
      }

      let error
      [error] = await to(this.$api.patchUser(this.user, user))
      if (error) return

      eventBus.$emit('message-success', this.$t('profile:success'))

      if (this.isAuthenticatedUser) {
        // If authenticated user: reload it
        this.$store.dispatch('getAuthenticatedUser')
      } else {
        // Else reload the page
        this.getUser()
      }
    }
  }
}
</script>
